import Box from '@mui/material/Box'
import { DeleteActionButton, EditActionButton, ViewActionButton } from '../../../../components/MRTDataGrid/TableActionButtons'
import Typography from '@mui/material/Typography'
import { MRT_Cell, MRT_ColumnDef } from 'material-react-table'
import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useLoggedInUser } from '../../../../app/hooks/useLoggedInUser'
import { useNotificationStack } from '../../../../app/hooks/useNotificationStack'
import { useConfirmDialogContext } from '../../../../components/ConfirmDialog'
import {
  useDeleteContactTypeMutation,
  useGetContactTypesByCustomerIdQuery,
  useGetContactTypesByTenantIdQuery,
} from '../../../../app/redux-fetch/apiCoreQuery'
import AddButton from '../../../../components/Buttons/AddButton'
import commonMessages from '../../../../components/Messages/commonMessages'
import MRTDataGridv2 from '../../../../components/MRTDataGrid/MRTDataGridv2'
import { TableTitleTypography } from '../../../../components/Typographies/TableTitleTypography'
import YesNoTypography from '../../../../components/YesNoTypograpphy/YesNoTypography'
import SideModalWrapper from '../../../Assets/modals/SideModalWrapper'
import { IContactType } from '../../../Contacts/types'
import messages from '../messages'
import ContactTypeForm from './ContactTypeForm'

const ContactTypeTab: React.FC = () => {
  const { formatMessage } = useIntl()
  const { tenantId, customerId } = useLoggedInUser()
  const [createOpen, setCreateOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [viewOpen, setViewOpen] = useState(false)
  const [selectedContactId, setSelectedContactId] = useState<number | null>(null)
  const { enqueueSuccess, enqueueFailure } = useNotificationStack()
  const { confirm } = useConfirmDialogContext()
  const [deleteContactType] = useDeleteContactTypeMutation()

  const {
    data: contactTypes = [],
    isFetching: contactsLoading,
    isSuccess: contactsLoaded,
  } = customerId
      ? useGetContactTypesByCustomerIdQuery(customerId)
      : useGetContactTypesByTenantIdQuery(tenantId)

  const columns = useMemo<MRT_ColumnDef<IContactType>[]>(
    () => [
      {
        accessorKey: 'name',
        header: formatMessage(commonMessages.name),
        Cell: ({ cell }: { cell: MRT_Cell<IContactType> }) => (
          <Typography>{cell.getValue<string>()}</Typography>
        ),
      },
      {
        header: formatMessage(messages.contactCategory),
        accessorFn: (row) => row.contactCategory?.name ?? '',
        id: 'contactCategoryName',
        Cell: ({ cell }: { cell: MRT_Cell<IContactType> }) => (
          <Typography>{cell.getValue<string>()}</Typography>
        ),
      },
      {
        accessorKey: 'systemDefined',
        header: formatMessage(messages.systemDefined),
        filterVariant: 'checkbox',
        muiTableHeadCellProps: { align: 'center' },
        muiTableBodyCellProps: { align: 'center' },
        Cell: ({ cell }: { cell: MRT_Cell<IContactType> }) => (
          <YesNoTypography isYes={cell.getValue<boolean>()} />
        ),
      },
      {
        accessorKey: 'enabled',
        header: formatMessage(messages.enabled),
        filterVariant: 'checkbox',
        muiTableHeadCellProps: { align: 'center' },
        muiTableBodyCellProps: { align: 'center' },
        Cell: ({ cell }: { cell: MRT_Cell<IContactType> }) => (
          <YesNoTypography isYes={cell.getValue<boolean>()} />
        ),
      },
      {
        accessorKey: 'inUse',
        header: formatMessage(messages.inUse),
        filterVariant: 'checkbox',
        muiTableHeadCellProps: { align: 'center' },
        muiTableBodyCellProps: { align: 'center' },
        Cell: ({ cell }: { cell: MRT_Cell<IContactType> }) => (
          <YesNoTypography isYes={cell.getValue<boolean>()} />
        ),
      },
    ],
    [formatMessage]
  )

  const handleDeleteContactType = async (id: number) => {
    const confirmed = await confirm({
      title: formatMessage(messages.deleteContactTypeTitle),
      message: formatMessage(messages.deleteContactTypeDescription),
      confirmText: formatMessage(messages.deleteBtn),
      cancelText: formatMessage(messages.cancelBtn),
      severity: 'warning',
      confirmButtonColor: 'error',
      cancelButtonColor: 'secondary',
    })

    if (confirmed) {
      try {
        await deleteContactType(id).unwrap()
        enqueueSuccess(messages.contactTypeDeleted)
      } catch (error) {
        enqueueFailure(messages.contactTypeNotDeleted)
      }
    }
  }

  const selectedContactType = contactTypes.find((c) => c.id === selectedContactId) ?? {
    tenantId: +tenantId,
    customerId: customerId,
    id: 0,
    name: '',
    contactCategoryId: 0,
    enabled: true,
  }

  return (
    <>
      <SideModalWrapper
        open={createOpen}
        key={'create'}
        headerLabel={formatMessage(messages.createEditContactType)}
        onClose={() => setCreateOpen(false)}
        withBottomBox={false}
      >
        <ContactTypeForm
          contactType={{
            tenantId: +tenantId,
            customerId: customerId,
            id: 0,
            name: '',
            contactCategoryId: 0,
            enabled: true,
          }}
          handleClose={() => setCreateOpen(false)}
        />
      </SideModalWrapper>
      <SideModalWrapper
        open={editOpen}
        key={'edit'}
        headerLabel={formatMessage(messages.createEditContactType)}
        onClose={() => setEditOpen(false)}
        withBottomBox={false}
      >
        <ContactTypeForm
          contactType={selectedContactType}
          handleClose={() => setEditOpen(false)}
        />
      </SideModalWrapper>
      <SideModalWrapper
        open={viewOpen}
        key={'view'}
        headerLabel={formatMessage(messages.viewContactType)}
        onClose={() => setViewOpen(false)}
        withBottomBox={false}
      >
        <ContactTypeForm
          contactType={selectedContactType}
          handleClose={() => setViewOpen(false)}
          isDisabled
        />
      </SideModalWrapper>
      <MRTDataGridv2
        initialState={{
          pagination: { pageSize: 100, pageIndex: 0 },
          density: 'comfortable',
          sorting: [{ id: 'name', desc: false }],
        }}
        leftHeadingComponent={
          <Box sx={{ display: 'inline-flex' }}>
            <TableTitleTypography>{formatMessage(messages.contacts)}</TableTitleTypography>
          </Box>
        }
        rightHeadingComponent={
          <AddButton
            text={formatMessage(messages.newContactBtn)}
            onClick={() => setCreateOpen(true)}
          />
        }
        state={{
          isLoading: contactsLoading || !contactsLoaded,
        }}
        data={contactTypes}
        columns={columns}
        getRowId={(row) => row.id?.toString()}
        enableEditing
        enableRowActions
        positionActionsColumn='last'
        renderRowActions={({ row }) => (
          <Box sx={{ display: 'flex' }}>
            <ViewActionButton
              onClick={() => {
                setSelectedContactId(row.original.id)
                setViewOpen(true)
              }}
            />
            <EditActionButton
              onClick={() => {
                setSelectedContactId(row.original.id)
                setEditOpen(true)
              }}
            />
            {!row.original.inUse && !row.original.systemDefined && (
              <DeleteActionButton
                onClick={() => handleDeleteContactType(row.original.id)}
              />
            )}
          </Box>
        )}
      />
    </>
  )
}

export default ContactTypeTab
